import {FieldValues} from 'react-hook-form';
import {RegisterOptions} from 'react-hook-form/dist/types/validator';
import {
    addressAdditionRegEx, bankAccountOwnerRegEx,
    cityRegEx, couponRegEx, dateDayMonthRegEx, dateYearRegEx, germanIbanRegEx,
    houseNumberRegEx,
    mobileNumberRegEx,
    nameRegEx,
    postalCodeRegEx,
    postalCodeRestRegEx,
    newPasswordRegEx,
    streetRegEx,
    telNumberRegEx
} from './regEx';
import {removeAllWhitespaces, trimWhitespaces} from './utils.js';
import {emailFormatValidator} from '../validation/email-validator';
import {maxLengthValidator} from '../validation/validators';

export const MAX_LENGTH_CONSTRAINTS = {
    FIRST_NAME: 20,
    LAST_NAME: 32,
    BANK_ACCOUNT_FULL_NAME: 52,
    EMAIL: 75,
    PASSWORD: 32,
    CITY: 32,
    STREET: 60,
    HOUSENUMBER: 8,
    ADDRESS_EXTRA_CO: 40,
    ADDRESS_GENERIC_EXTRA: 50,
};

export const MIN_LENGTH_CONSTRAINTS = {
    PASSWORD : 8,
    FIRST_NAME: 2,
    LAST_NAME: 2,
};

const validateEmailFormat = emailFormatValidator();
const validateEmailMaxLength = maxLengthValidator(MAX_LENGTH_CONSTRAINTS.EMAIL);

const validatePackingStation = (value: string) => !['packstation', 'pakstation', 'postfiliale', 'pack station', 'post filiale'].includes(value.trim().toLowerCase());


export declare interface SalutationValidationTexts {
    'required.salutation': string,
}

export const salutationValidation = (texts: SalutationValidationTexts): RegisterOptions<FieldValues, string> => {
    return {required: texts['required.salutation']};
};


export declare interface IbanValidationTexts {
    'cot_bank_collection_iban_required': string,
}

export const ibanValidation = (texts : IbanValidationTexts): RegisterOptions<FieldValues, string> => {
    return {
        validate: {
            required: v => Boolean(trimWhitespaces(v)) || texts['cot_bank_collection_iban_required'],
            format: v => germanIbanRegEx.test(removeAllWhitespaces(v)) || texts['cot_bank_collection_iban_required']
        }
    };
};

export declare interface BankAccountOwnerValidationTexts {
    'required.currentOrder.bankAccount.owner': string,
    'cot_bank_collection_owner_invalid': string,
    'cot_bank_collection_owner_max_length': string,
}

export const bankAccountOwnerValidation = (texts: BankAccountOwnerValidationTexts): RegisterOptions<FieldValues, string> => {
    return {
        validate: {
            required: v => Boolean(trimWhitespaces(v)) || texts['required.currentOrder.bankAccount.owner'],
            format: v => bankAccountOwnerRegEx.test(trimWhitespaces(v)) || texts['cot_bank_collection_owner_invalid'],
            maxLength: v => v.length <= MAX_LENGTH_CONSTRAINTS.BANK_ACCOUNT_FULL_NAME || texts['cot_bank_collection_owner_max_length']
        }
    };
};

export declare interface FirstNameValidationTexts {
    'first_name_is_required': string,
    'firstname.invalid': string,
    'first_name_max_length_exceeded': string,
}

export const firstNameValidation = (texts: FirstNameValidationTexts): RegisterOptions<FieldValues, string> => {
    return {
        validate: {
            required: v => Boolean(trimWhitespaces(v)) && v.length >= MIN_LENGTH_CONSTRAINTS.FIRST_NAME || texts['first_name_is_required'],
            format: v => nameRegEx.test(trimWhitespaces(v)) || texts['firstname.invalid'],
            maxLength: v => v.length <= MAX_LENGTH_CONSTRAINTS.FIRST_NAME || texts['first_name_max_length_exceeded']
        }
    };
};

export declare interface LastNameValidationTexts {
    'required.lastName': string,
    'lastname.invalid': string,
    'last_name_max_length_exceeded': string,
}

export const lastNameValidation = (texts: LastNameValidationTexts): RegisterOptions<FieldValues, string> => {
    return {
        validate: {
            required: v => Boolean(trimWhitespaces(v)) && v.length >= MIN_LENGTH_CONSTRAINTS.LAST_NAME || texts['required.lastName'],
            format: v => nameRegEx.test(trimWhitespaces(v)) || texts['lastname.invalid'],
            maxLength: v => v.length <= MAX_LENGTH_CONSTRAINTS.LAST_NAME || texts['last_name_max_length_exceeded']
        }
    };
};

export declare interface EmailValidationTexts {
    'email_is_required': string,
    'email.invalid': string,
    'email_max_length_exceeded': string,
}

export const emailValidation = (texts: EmailValidationTexts, alreadyUsedEmail?: string): RegisterOptions<FieldValues, string> => {
    return {
        validate: {
            required: v => Boolean(v) || Boolean(alreadyUsedEmail) || texts['email_is_required'],
            format: v => (v ? validateEmailFormat(v) : validateEmailFormat(alreadyUsedEmail)) || texts["email.invalid"],
            maxLength: v => (v ? validateEmailMaxLength(v) : validateEmailMaxLength(alreadyUsedEmail)) || texts["email_max_length_exceeded"],
        }
    };
};

export declare interface RepeatedEmailValidationTexts {
    'email_is_required': string,
    'email.invalid': string,
    'email_max_length_exceeded': string,
    'presignup.emailrepeat.invalid': string,
}

export function repeatedEmailValidation(texts: RepeatedEmailValidationTexts, comparisonEmailValue): RegisterOptions<FieldValues, string> {
    return {
        validate: {
            required: v => Boolean(v) || texts['email_is_required'],
            format: v => validateEmailFormat(v) || texts['email.invalid'],
            maxLength: v => validateEmailMaxLength(v) || texts['email_max_length_exceeded'],
            notEqual: v => v === comparisonEmailValue || comparisonEmailValue.length === 0 || texts['presignup.emailrepeat.invalid']
        }
    };
}

export declare interface PasswordValidationTexts {
    'required.password': string,
    'password_length_at_least_6_chars': string,
    'password_max_length_exceeded': string,
}

export const passwordValidation = (texts: PasswordValidationTexts): RegisterOptions<FieldValues, string> => {
    return {
        validate: {
            required: v => Boolean(v) || texts['required.password'],
            minLength: v => v.length >= 6 || texts['password_length_at_least_6_chars'],
            maxLength: v => v.length <= MAX_LENGTH_CONSTRAINTS.PASSWORD || texts['password_max_length_exceeded']
        }
    };
};

export declare interface NewPasswordValidationTexts {
    'password_is_required': string,
    'pwd_unmet_reqs': string,
    'password_length_at_least_8_chars': string,
    'password_max_length_exceeded': string,
}

export const newPasswordValidation = (texts: NewPasswordValidationTexts): RegisterOptions<FieldValues, string> => {
    return {
        validate: {
            required: v => Boolean(v) || texts['password_is_required'],
            format: v => newPasswordRegEx.test(v) || texts['pwd_unmet_reqs'],
            minLength: v => v.length >= MIN_LENGTH_CONSTRAINTS.PASSWORD || texts['password_length_at_least_8_chars'],
            maxLength: v => v.length <= MAX_LENGTH_CONSTRAINTS.PASSWORD || texts['password_max_length_exceeded']
        }
    };
};

export declare interface PostalCodeValidationTexts {
    'required.postalCode': string,
    'invalid.postalCode': string,
}

export const postalCodeValidation = (texts: PostalCodeValidationTexts, country: string): RegisterOptions<FieldValues, string> => {

    const formatRegEx = !!postalCodeRegEx[country] ? postalCodeRegEx[country] : postalCodeRestRegEx;

    return {
        validate: {
            required: v => Boolean(v) || texts['required.postalCode'],
            format: v => formatRegEx.test(v) || texts['invalid.postalCode']
        }
    };
};

export declare interface CityValidationTexts {
    'required.city': string,
    'city.invalid': string,
    'city_max_length_exceeded': string,
}

export const cityValidation = (texts: CityValidationTexts): RegisterOptions<FieldValues, string> => {
    return {
        validate: {
            required: v => Boolean(trimWhitespaces(v)) || texts['required.city'],
            format: v => cityRegEx.test(trimWhitespaces(v)) || texts['city.invalid'],
            maxLength: v => v.length <= MAX_LENGTH_CONSTRAINTS.CITY || texts['city_max_length_exceeded']
        }
    };
};

export declare interface StreetValidationTexts {
    'required.postalCodeForm.street': string,
    'street_invalid_format': string,
    'street_max_length_exceeded': string,
    'packstation.in.field': string,
}

export const streetValidation = (texts: StreetValidationTexts): RegisterOptions<FieldValues, string> => {
    return {
        validate: {
            required: v => Boolean(trimWhitespaces(v)) || texts['required.postalCodeForm.street'],
            format: v => streetRegEx.test(v) || texts['street_invalid_format'],
            maxLength: v => v.length <= MAX_LENGTH_CONSTRAINTS.STREET || texts['street_max_length_exceeded'],
            packingStation: v => validatePackingStation(v) || texts['packstation.in.field']
        }
    };
};

export declare interface HouseNumberValidationTexts {
    'housenumber_is_required': string,
    'housenumber_invalid_format': string,
    'housenumber_max_length_exceeded': string,
}

export const houseNumberValidation = (texts: HouseNumberValidationTexts): RegisterOptions<FieldValues, string> => {
    return {
        validate: {
            required: v => Boolean(v) || texts['housenumber_is_required'],
            format: v => houseNumberRegEx.test(trimWhitespaces(v)) || texts['housenumber_invalid_format'],
            maxLength: v => v.length <= MAX_LENGTH_CONSTRAINTS.HOUSENUMBER || texts['housenumber_max_length_exceeded']
        }
    };
};

export declare interface AddressAdditionCoValidationTexts {
    'address_extra_co_max_length_exceeded': string,
    'address_addition_validation_error': string,
    'packstation.in.field': string,
}

export const addressAdditionCoValidation = (texts: AddressAdditionCoValidationTexts): RegisterOptions<FieldValues, string> => {
    return {
        validate: {
            maxLength: v => v.length <= MAX_LENGTH_CONSTRAINTS.ADDRESS_EXTRA_CO || texts['address_extra_co_max_length_exceeded'],
            format: v => addressAdditionRegEx.test(trimWhitespaces(v)) || texts['address_addition_validation_error'],
            packingStation: v => validatePackingStation(v) || texts['packstation.in.field']
        }
    };
};

export declare interface AddressGenericAdditionValidationTexts {
    'address_extra_generic_max_length_exceeded': string,
    'address_generic_addition_validation_error': string,
    'packstation.in.field': string,
}

export const addressGenericAdditionValidation = (texts: AddressGenericAdditionValidationTexts): RegisterOptions<FieldValues, string> => {
    return {
        validate: {
            maxLength: v => v.length <= MAX_LENGTH_CONSTRAINTS.ADDRESS_GENERIC_EXTRA || texts['address_extra_generic_max_length_exceeded'],
            format: v => addressAdditionRegEx.test(trimWhitespaces(v)) || texts['address_generic_addition_validation_error'],
            packingStation: v => validatePackingStation(v) || texts['packstation.in.field']
        }
    };
};

export declare interface BirthdayValidationTexts {
    'required.customerForm.birthDate': string,
    'birthDate.invalid': string,
}

export const birthdayDayValidation = (texts: BirthdayValidationTexts): RegisterOptions<FieldValues, string> => {
    return {
        validate: {
            required: v => Boolean(v) || texts['required.customerForm.birthDate'],
            maxLength: v => v.length <= 2 || texts['birthDate.invalid'],
            minValue: v => parseInt(v, 10) > 0 || texts['birthDate.invalid'],
            maxValue: v => parseInt(v, 10) <= 31 || texts['birthDate.invalid'],
            format: v => dateDayMonthRegEx.test(v) || texts['birthDate.invalid']
        }
    };
};

export const birthdayMonthValidation = (texts: BirthdayValidationTexts): RegisterOptions<FieldValues, string> => {
    return {
        validate: {
            required: v => Boolean(v) || texts['required.customerForm.birthDate'],
            maxLength: v => v.length <= 2 || texts['birthDate.invalid'],
            minValue: v => parseInt(v, 10) > 0 || texts['birthDate.invalid'],
            maxValue: v => parseInt(v, 10) <= 12 || texts['birthDate.invalid'],
            format: v => dateDayMonthRegEx.test(v) || texts['birthDate.invalid']
        }
    };
};

export const birthdayYearValidation = (texts: BirthdayValidationTexts): RegisterOptions<FieldValues, string> => {
    return {
        validate: {
            required: v => Boolean(v) || texts['birthDate.invalid'],
            length: v => v.length === 4 || texts['birthDate.invalid'],
            minValue: v => parseInt(v, 10) >= 1900 || texts['birthDate.invalid'],
            maxValue: v => parseInt(v, 10) <= new Date().getFullYear() || texts['birthDate.invalid'],
            format: v => dateYearRegEx.test(v) || texts['birthDate.invalid']
        }
    };
};

export declare interface CouponValidationTexts {
    cot_coupon_invalid_or_unknown: string,
}

export const couponValidation = (texts: CouponValidationTexts): RegisterOptions<FieldValues, string> => {
    return {
        validate: {
            format: v => couponRegEx.test(trimWhitespaces(v)) || texts.cot_coupon_invalid_or_unknown,
            length: v => v.length <= 24 || texts.cot_coupon_invalid_or_unknown
        }
    };
};

export declare interface TelNumberValidationTexts {
    'required.handyNumber_bpde_sap': string,
}

export const telNumberValidation = (texts: TelNumberValidationTexts): RegisterOptions<FieldValues, string> => {
    return {
        validate: {
            required: v => Boolean(v) || texts['required.handyNumber_bpde_sap'],
            format: v => telNumberRegEx.test(v) || texts['required.handyNumber_bpde_sap']
        }
    };
};

export const personalDataTelNumberValidation = (texts: TelNumberValidationTexts): RegisterOptions<FieldValues, string> => {
    return {
        validate: {
            format: v => telNumberRegEx.test(v) || '' === v || texts['required.handyNumber_bpde_sap']
        }
    };
};

export const mobileNumberValidation = (texts: TelNumberValidationTexts): RegisterOptions<FieldValues, string> => {
    return {
        validate: {
            format: v => mobileNumberRegEx.test(v) || '' === v || texts['required.handyNumber_bpde_sap']
        }
    };
};
